import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiExternalLink, FiArrowRight, FiTarget, FiTrendingUp } = FiIcons;

const CaseStudies = () => {
  const [filter, setFilter] = useState('All');

  const categories = ['All', 'Hospitality', 'AI & Data', 'Cloud Migration'];

  const projects = [
    {
      id: 1,
      title: "Hotelyzer Ecosystem",
      category: "Hospitality",
      description: "A comprehensive digital transformation for the hospitality sector, focusing on real-time guest engagement and operational efficiency.",
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80",
      stats: "45% Revenue Growth",
      tags: ["Cloud-Native", "React", "Node.js"]
    },
    {
      id: 2,
      title: "BookMeIn.Events",
      category: "Hospitality",
      description: "Automated ticketing and event management platform designed to handle high-concurrency traffic during major event launches.",
      image: "https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=800&q=80",
      stats: "2M+ Tickets Processed",
      tags: ["Scalable Arch", "Redis", "AWS"]
    },
    {
      id: 3,
      title: "Predictive Supply Chain",
      category: "AI & Data",
      description: "AI-driven demand forecasting system that optimized inventory levels for a regional manufacturing leader.",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&q=80",
      stats: "30% Waste Reduction",
      tags: ["TensorFlow", "Python", "BigData"]
    },
    {
      id: 4,
      title: "FinTech Cloud Pivot",
      category: "Cloud Migration",
      description: "Migrating legacy banking systems to a secure, compliant hybrid cloud infrastructure with zero downtime.",
      image: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&q=80",
      stats: "99.99% Reliability",
      tags: ["Kubernetes", "Security", "Azure"]
    }
  ];

  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(p => p.category === filter);

  return (
    <section id="cases" className="py-24 px-6 relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-2xl"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Our <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">Success Stories</span>
            </h2>
            <p className="text-xl text-gray-300">
              Explore how we've helped businesses navigate the complexities of digital evolution through custom-engineered solutions.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === cat 
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30' 
                    : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <motion.div 
          layout
          className="grid md:grid-cols-2 gap-8"
        >
          <AnimatePresence mode='popLayout'>
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="group relative bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden hover:border-white/20 transition-all duration-500"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                </div>

                <div className="p-8 relative">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">
                        {project.category}
                      </span>
                      <h3 className="text-2xl font-bold text-white mt-1">{project.title}</h3>
                    </div>
                    <div className="bg-white/10 p-3 rounded-2xl">
                      <SafeIcon icon={FiExternalLink} className="text-white text-xl" />
                    </div>
                  </div>

                  <p className="text-gray-300 mb-6 line-clamp-2">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {project.tags.map(tag => (
                      <span key={tag} className="text-xs px-3 py-1 rounded-full bg-white/5 text-gray-400 border border-white/10">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-6 border-t border-white/10">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 bg-green-500/20 rounded-lg">
                        <SafeIcon icon={FiTrendingUp} className="text-green-400" />
                      </div>
                      <span className="text-white font-bold">{project.stats}</span>
                    </div>
                    <motion.button
                      whileHover={{ x: 5 }}
                      className="flex items-center space-x-2 text-indigo-400 font-semibold"
                    >
                      <span>Case Details</span>
                      <SafeIcon icon={FiArrowRight} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudies;